import React, { 
    useState, 
    useEffect,
    useRef
} from 'react';
  
import Navbar from '../../Components/Navbar/Navbar'
import dayjs from 'dayjs';

import { 
    Layout,
    Col, 
    Row,
    Button,
    Space,
    Modal,
    Form,
    Input,
    Table,
    Menu,
    Dropdown,
    DatePicker,
} from 'antd';
import { 
    ProductOutlined,
    DownOutlined,
    MoreOutlined,
    ExclamationCircleFilled 
} from '@ant-design/icons';
import { UseGetRequest } from '../../Utils/api/ApiRequests'; 
import { usePostRequest } from '../../hooks/usePostRequest';
import { 
    useNavigate, 
    useLocation 
} from "react-router-dom";
import { formatDate, getTodayDateISO } from '../../Utils/Common';
import BtnExcel from '../../Components/BtnExcel';
import BillingStatement from '../../Components/BillingStatement';
import { useReactToPrint } from 'react-to-print';
  
const { Item } = Form;
const { confirm } = Modal;
const { Search } = Input;
const { RangePicker } = DatePicker;
  
const Billing = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [billingForm] = Form.useForm();
    const [fetching, setFetching] = useState(false);
    const [billingModal, setBillingModal] = useState(false);
    const [dataSource, setDataSource] = useState({ data: [], size: 0 });
    const [excelData, setExcelData] = useState([]);
    const [filterConfig, setFilterConfig] = useState({
        page: 1,
        data_size: 10,
        term: '',
        billing_date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
        billing_date_to: dayjs().endOf('month').format('YYYY-MM-DD'),
    });

    const statementsRef = useRef(null);
    const handlePrintSOA = useReactToPrint({
        contentRef: statementsRef,
        documentTitle: `Statements-of-Account-${getTodayDateISO()}`,
        bodyClass: 'soa-print',
    });

    const { callPost } = usePostRequest(()=>{
        billingForm.resetFields();
        setBillingModal(false);
        fetchBillingData();
    });
  
    // Dispatch the selected billing action while retaining the existing export handler.
    const handleActionClick = (key, onExport) => {
        switch (key) {
            case 'generate':
                setBillingModal(true);
                break;
            case 'export':
                onExport();
                break;
            case 'print':
                handlePrintSOA();
                break;
            default:
                break;
        }
    };

    const customSpinner = <ProductOutlined style={{ fontSize: 24, color: "#005E30"  }} spin />;

    const columns = [
        {
            title: 'Member Name',
            dataIndex: 'member_name',
            key: 'member_name',
            align: 'center',
        }, 
        {
            title: 'AR Customer ID',
            dataIndex: 'ar_cust_id',
            key: 'ar_cust_id',
            align: 'center',
        },
        {
            title: 'Posting Date',
            dataIndex: 'posting_date',
            key: 'posting_date',
            align: 'center',
            render: (date) => formatDate(date),
        },
        {
            title: 'Billing Date',
            dataIndex: 'billing_date',
            key: 'billing_date',
            align: 'center',
            render: (date) => formatDate(date),
        },
        {
            title: 'Added By',
            dataIndex: 'added_by_user',
            key: 'added_by_user',
            align: 'center',
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            align: 'center',
            width: '5%',
            render: (_, record) => (
                <ActionBtn row={record} />
            ),
        },
    ];

    function ActionBtn(row) {
        const menu = (
            <Menu>
                
                <Menu.Item 
                    onClick={() => navigate('/accounting/view-billing/' + row.row.id)}
                >
                    View
                </Menu.Item>
                <Menu.Item 
                    key="delete"
                    style={{color: 'red'}}
                    onClick={() => showDeleteConfirm(row.row.id)}
                >
                    Delete
                </Menu.Item>
                 
            </Menu>
        );

        return (
            <>
                <span className='center'>
                    <Dropdown
                        overlay={menu} 
                        placement="bottomLeft"
                        trigger={['click']}
                    >
                    <div type='primary' onClick={(e) => e.preventDefault()} className='action-btn'>
                        <Space>
                        <MoreOutlined />
                        </Space>
                    </div>
                    </Dropdown>
                </span>
            </>
        );
    };

    const showDeleteConfirm = (id) => {
        const finalValues = Object.entries({billing_id: id}).map(([key, value]) => ({
            key,
            value,
        }));

        confirm({
          title: 'Do you want to permanently remove this Billing?',
          icon: <ExclamationCircleFilled className='red-cont'/>,
          content: 'This action cannot be undone.',
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          width: 600,
          okButtonProps: {
            className: 'cancel-antbtn',
          },
          cancelButtonProps: {
            className: 'save-antbtn',
          },
          
          onOk() {
            return callPost(
                `billings/delete`, 
                finalValues,
                true
            );
          },
        });
    };
  
    // Fetch once so the table, Excel export, and SOA print use identical records.
    async function fetchBillingData() {
      setFetching(true);
      const response = await UseGetRequest("billings/get_billing", filterConfig);
      if (response?.data) {
        // RULES: The API filters dates but ignores search text; apply search before all outputs.
        const term = filterConfig.term.trim().toLowerCase();
        const filteredBillings = (response.data.billings || []).filter((billing) =>
            !term || [billing.member_name, billing.ar_cust_id].some((value) =>
                String(value || '').toLowerCase().includes(term)
            )
        );
        setDataSource({ data: filteredBillings, size: filteredBillings.length });
        const exportData = filteredBillings.map((data) => ({
            member_name: data.member_name,
            ar_cust_id: data.ar_cust_id,
            posting_date: formatDate(data.posting_date),
            billing_date: formatDate(data.billing_date),
            added_by_user: data.added_by_user,
        }));
        setExcelData(exportData);
      } else {
        setDataSource({ data: [], size: 0 });
        setExcelData([]);
      }
      setFetching(false);
    }

    // Validate setup and carry the selected dates to the review page.
    const handleProceed = async () => {
        try {
            const values = await billingForm.validateFields();
            const billingConfig = {
                period_covered: values.period_covered.format('YYYY-MM-DD'),
                posting_date: values.posting_date.format('YYYY-MM-DD'),
                billing_date: values.billing_date.format('YYYY-MM-DD'),
                is_jun_golf: 0,
                is_christmas_contribution: 0,
                is_annual_registration: 0,
            };
            navigate('/accounting/generate-billings', { state: { billingConfig } });
        } catch (error) {
            // Form validation displays the missing fields in the setup modal.
        }
    };

    const handleTableChange = (pagination) => {
      setFilterConfig((prev) => ({
        ...prev,
        page: parseInt(pagination.current),
        data_size: pagination.pageSize,
      }));
    };

    const handleChange = (e) => {
        setFilterConfig((prev) => (
          {
            ...prev,
            term: e || '',
            page: 1,
          }
        ));
    };
      
    const handleEmpty = (e) => {
        if(e.target.value === ''){
          setFilterConfig((prev) => (
            {
              ...prev,
              term: e.target.value || '',
              page: 1,
            }
          ));
        }
    };

    const onSearchDate = (e) => {
        if(e){
            setFilterConfig((prev) => (
                {
                    ...prev,
                    billing_date_from: e[0].format("YYYY-MM-DD"),
                    billing_date_to: e[1].format("YYYY-MM-DD"),
                    page: 1,
                }
            ));
        } else {
            setFilterConfig((prev) => (
                {
                    ...prev,
                    billing_date_from: '',
                    billing_date_to: '',
                    page: 1,
                }
            ));
            
        }
    };
    
    useEffect(() => {
        if (!billingModal) {
          window.history.replaceState({}, document.title, location.pathname);          
          setBillingModal(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingModal]);
      
    useEffect(() => {
        fetchBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterConfig]);
    
    return (
        <Layout className='page'>
            <Navbar navKey={'credit_billing'}/>
            <Layout className='content-container overflow-y'>
                <Row className='custom-padding responsive-tab-inline'>
                    <Col span={15} className='header-container'>
                        BILLINGS
                    </Col>
                    <Col span={9} className='mt-2 responsive-tab-width' align='end'>
                        <Space className='responsive-inline'> 
                            <Search
                                placeholder="Search Billing"
                                size='medium'
                                onSearch={(e)=>handleChange(e)}
                                onChange={(e)=>handleEmpty(e)}
                                className='responsive-header-input'
                            />
                            <BtnExcel
                                label={"Export to Excel"}
                                size={"sm"}
                                excelData={excelData}
                                xlsxHeaders={[
                                    "Member Name",
                                    "AR Customer ID",
                                    "Posting Date",
                                    "Billing Date",
                                    "Billing Date",
                                    "Added By",
                                ]}
                                handleData={fetchBillingData}
                                isClicked={fetching}
                                fileName={`Billings_${getTodayDateISO()}.xlsx`}
                                renderTrigger={({ onExport, isExporting }) => (
                                    <Dropdown
                                        trigger={['click']}
                                        placement="bottomRight"
                                        menu={{
                                            items: [
                                                { key: 'generate', label: 'Generate' },
                                                { key: 'export', label: 'Export to Excel', disabled: isExporting },
                                                { key: 'print', label: 'Print SOA', disabled: fetching || !dataSource.data.length },
                                            ],
                                            onClick: ({ key }) => handleActionClick(key, onExport),
                                        }}
                                    >
                                        <Button
                                            className="button-color responsive-header-btn"
                                            type="primary"
                                        >
                                            <Space>Select Action <DownOutlined /></Space>
                                        </Button>
                                    </Dropdown>
                                )}
                            />        
                        </Space>
                    </Col>
                </Row>
                <Row className='ms-4 responsive-tab-inline'>
                    <Col span={24} className='responsive-tab-width'>
                        <Space span={24}>
                            <RangePicker 
                                renderExtraFooter={() => 'extra footer'} 
                                className='responsive-header-input'
                                value={[
                                    filterConfig?.billing_date_from ? dayjs(filterConfig.billing_date_from) : null,
                                    filterConfig?.billing_date_to ? dayjs(filterConfig.billing_date_to) : null
                                ]}
                                onChange={(e)=>onSearchDate(e)}
                            />
                        </Space>
                    </Col>
                </Row>
                <Row className='page-content'>
                    <Col span={24} className='responsive-overflow-auto'>
                        <Table
                            rowKey="id"
                            columns={columns} 
                            dataSource={dataSource.data}
                            loading={{
                            indicator: customSpinner,
                            spinning: fetching,
                            }}
                            pagination={{
                            defaultPageSize: 10,
                            //   total: pageSize,
                            current: filterConfig?.page,
                            pageSize: filterConfig.data_size,
                            }}
                            onChange={handleTableChange}
                        />
                    </Col>
                </Row>
                <div className="d-none">
                    <div ref={statementsRef}>
                        {dataSource.data.map((billing) => (
                            <BillingStatement key={billing.id} billing={billing} />
                        ))}
                    </div>
                </div>
                <Modal
                    title="Generate Billing"
                    open={billingModal}
                    onOk={handleProceed}
                    onCancel={() => {
                        setBillingModal(false);
                        billingForm.resetFields();
                    }}
                    okText="Proceed"
                    maskClosable={false}
                    width={800} 
                >
                    <Form 
                        form={billingForm} 
                        layout="vertical"
                    >
                        <Row>
                           
                            <Col span={8} className='p-2'>
                                <Item
                                    label="Period Covered"
                                    name="period_covered"
                                    rules={[{ required: true, message: 'Please select a Period Covered' }]}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Item>
                            </Col>
                            <Col span={8} className='p-2'>
                                <Item
                                    label="Posting Date"
                                    name="posting_date"
                                    rules={[{ required: true, message: 'Please select a Posting Date' }]}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Item>
                            </Col>
                            <Col span={8} className='p-2'>
                                <Item
                                    label="Billing Date"
                                    name="billing_date"
                                    rules={[{ required: true, message: 'Please select a Billing Date' }]}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </Layout>
        </Layout>
    )
  }

  export default Billing
